import { useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Boxes,
  ClipboardList,
  Copy,
  Download,
  DraftingCompass,
  ExternalLink,
  Factory,
  FileText,
  Image as ImageIcon,
  Layers,
  Loader2,
  Package,
  Paperclip,
  Pencil,
  Plus,
  ShoppingCart,
  Trash2,
  Truck,
  Warehouse,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { FolderUploadButton } from '@/components/ui/FolderUploadButton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/Select';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  formatCurrency,
  formatDate,
  formatDateOnly,
  formatDateTime,
  formatUnitCurrency,
} from '@/lib/formatters';
import {
  breakdownInPurchaseUnits,
  describePricingUnit,
  describePurchaseUnit,
  formatQtyUom,
  formatUnitCostCents,
  isPackUnit,
  mainPurchaseUnit,
  pluralUom,
  supplierPricingUnit,
  supplierPurchaseUnit,
  toPriceUomCents,
  uomChoices,
  type PurchaseUnit,
} from '@/lib/uom';
import {
  PO_CLOSED_STATUSES,
  PO_STATUS_LABELS,
  PO_STATUS_VARIANTS,
  TRACKING_STATUS_LABELS,
  avgLandedCostParts,
  lastLandedCostParts,
  purchaseMethodLabel,
  purchaseOrderTrackingUrl,
  receiptLandedCostParts,
  useItemCostHistory,
  useItemCostSummary,
  useItemCurrentCost,
  itemCostSourceLabel,
  usePurchaseCards,
  type LandedCostParts,
  type PoStatus,
} from '@/features/procurement/hooks';
import { hasBomLines, makeCostResolver, useBomGraph } from '@/features/manufacturing/boms';
import { defaultOverstockAt } from '@/features/inventory/stockStatus';
import {
  useBomForItem,
  useBomsContainingItem,
  useDeleteItemDocument,
  useInventoryLevels,
  useItem,
  useItemConsumingWorkOrders,
  useItemDocuments,
  useItemDocumentSignedUrl,
  useItemKinds,
  useItemProducingWorkOrders,
  useItemProductionOrders,
  useItemPurchaseOrderLines,
  ON_ORDER_PRODUCTION_STATUSES,
  useItemSalesOrderLines,
  useItemSuppliers,
  useUpdateItem,
  useUploadItemDocument,
  useItems,
  type Item,
  type ItemDocument,
  type ItemDocumentKind,
  type ItemSupplier,
  type UpdateItemPatch,
} from '@/features/inventory/hooks';
import {
  EditableBool,
  EditableCategory,
  EditableCurrency,
  EditableNumber,
  EditableSelect,
  EditableText,
} from '@/components/inventory/EditableItemField';
import { useWorkResources } from '@/features/manufacturing/schedules';
import { ArchivedStockBanner, ItemStatusControl } from '@/components/inventory/ItemStatusControl';
import { KindPicker } from '@/components/inventory/KindPicker';
import { TagsPicker } from '@/components/inventory/TagsPicker';
import { WorkstationsPicker } from '@/components/inventory/WorkstationsPicker';
import { AddToBomDialog, type AddToBomMode } from '@/components/inventory/AddToBomDialog';
import { AddSupplierDialog } from '@/components/inventory/AddSupplierDialog';
import { DuplicateItemDialog } from '@/components/inventory/DuplicateItemDialog';
import { ItemPhotosCard } from '@/components/inventory/ItemPhotosCard';
import { ItemMovementsCard } from '@/components/inventory/ItemMovementsCard';
import { ItemEngineeringQuickLink, ItemEngineeringTab } from '@/components/engineering/ItemEngineeringTab';
import { useCanSeeEngineering } from '@/features/engineering/hooks';
import { useAuth } from '@/app/AuthProvider';
import { canWrite, type Role } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { websiteHref, websiteLabel } from '@/features/software/model';
// =====================================================================
// Per-kind configuration
// =====================================================================
//
// Each item kind cares about a different slice of the ERP. A raw
// material has no retail price; a finished good has no upstream BOM
// (it IS the top of a BOM tree); WIP is internal-only and never sells
// to a customer. The detail page therefore picks the Overview
// composition and the visible tabs from a per-kind config rather than
// rendering the same chrome for every SKU.
//
// To customize a kind:
//   1. Swap its `Overview` for a kind-specific component.
//   2. Trim its `tabs` array down to what's relevant.
//   3. Add new cards inline in that kind's Overview component — they
//      can pull from any of the existing hooks in `features/inventory`.

const TAB_DEFINITIONS = [
  { id: 'overview', label: 'Overview', icon: Package },
  { id: 'boms', label: 'BOMs', icon: Layers },
  { id: 'suppliers', label: 'Suppliers', icon: Factory },
  { id: 'inventory', label: 'Inventory', icon: Warehouse },
  { id: 'sales', label: 'Sales', icon: ShoppingCart },
  { id: 'procurement', label: 'Procurement', icon: Truck },
  { id: 'manufacturing', label: 'Manufacturing', icon: ClipboardList },
  { id: 'engineering', label: 'Engineering', icon: DraftingCompass },
  { id: 'documents', label: 'Documents', icon: FileText },
] as const;

type TabId = (typeof TAB_DEFINITIONS)[number]['id'];

const ALL_TAB_IDS = TAB_DEFINITIONS.map((t) => t.id) as readonly TabId[];

interface ItemKindConfig {
  /** Display name shown in the page header and Overview KV table. */
  label: string;
  /** Tabs visible for this kind, in render order. */
  tabs: readonly TabId[];
  /** Overview tab composition. Swap this to give a kind its own cards. */
  Overview: React.ComponentType<{ item: Item }>;
}

/**
 * Per-kind config keyed by the kind's slug (the column value persisted
 * on items.kind). The four baseline slugs map to their dedicated
 * Overview components; anything else (including custom kinds added by
 * a super admin via the new `item_kinds` catalog) falls through to
 * `FALLBACK_KIND_CONFIG` so the page stays functional without code
 * changes.
 *
 * `label` here is only used as the Overview component's section
 * heading; the page header itself reads the live label from the
 * kinds catalog so renames propagate without a code change.
 */
const ITEM_KIND_CONFIG: Record<string, ItemKindConfig> = {
  // The three surviving slugs after migration 91. The canonical label
  // is sourced from the live kinds catalog at render time; these static
  // strings only show up in the tiny window before that query resolves.
  raw_stock: {
    label: 'Raw material',
    tabs: ALL_TAB_IDS,
    Overview: RawStockOverview,
  },
  // Note: the user-facing label for the `raw_material` slug is
  // "Components" as of migration 51 — the slug stays for FK stability
  // while the seed row's label was rewritten.
  raw_material: {
    label: 'Components',
    tabs: ALL_TAB_IDS,
    Overview: RawMaterialOverview,
  },
  finished_good: {
    label: 'Finished good',
    tabs: ALL_TAB_IDS,
    Overview: FinishedGoodOverview,
  },
};

const FALLBACK_KIND_CONFIG: ItemKindConfig = {
  label: 'Item',
  tabs: ALL_TAB_IDS,
  Overview: DefaultOverview,
};

function getItemKindConfig(kind: string): ItemKindConfig {
  return ITEM_KIND_CONFIG[kind] ?? FALLBACK_KIND_CONFIG;
}

/**
 * The "360 view" for a single item — every operational surface that
 * touches this SKU is one click away. Tabs are mirrored to `?tab=...`
 * so a deep link can land on any panel and so the back button feels
 * right.
 *
 * Data is loaded per-tab via individual hooks. They run in parallel on
 * mount (React Query is lazy across hook calls but cheap to fan out)
 * because the panel headers benefit from showing counts even when the
 * tab itself isn't visible.
 */
export function ItemDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();

  const { role } = useAuth();
  const canSeeEngineering = useCanSeeEngineering();
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  const { data: item, isLoading, error } = useItem(id);
  // The kinds catalog is loaded eagerly (not gated on a tab) so the
  // page header can render the catalog label as soon as the item
  // resolves. The KindPicker inside the details card refetches
  // through the same hook — react-query dedupes the network call.
  const { data: kindsCatalog } = useItemKinds();

  if (error) {
    return (
      <div className="space-y-6">
        <BackLink />
        <PageHeader
          title="Item"
        />
      </div>
    );
  }

  if (isLoading || !item) {
    return (
      <div className="space-y-6">
        <BackLink />
        <PageHeader title="Loading item…" />
        <Card>
          <CardContent className="space-y-3 pt-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  const config = getItemKindConfig(item.kind);

  // Only render the tabs allowed for this kind. If the URL points to a
  // tab the current kind doesn't expose (e.g. someone bookmarked the
  // Sales tab on a raw material we later removed from that kind's
  // config), fall back to Overview rather than rendering a blank panel.
  // The Engineering tab also honours the per-person section grant so
  // people without the Engineering section never see an empty panel.
  const allowedTabs = config.tabs.filter((t) => t !== 'engineering' || canSeeEngineering);
  const visibleTabs = TAB_DEFINITIONS.filter((t) => allowedTabs.includes(t.id));
  const tabParam = params.get('tab') as TabId | null;
  const active: TabId = tabParam && allowedTabs.includes(tabParam) ? tabParam : 'overview';

  const OverviewForKind = config.Overview;

  // Show the catalog label in the page header so a renamed kind
  // surfaces here too. Falls back to the per-kind config's static
  // label for the tiny window between mount and the kinds query
  // resolving (or for kinds we don't recognise).
  const assignedKinds = [item.kind, ...(item.additional_kinds ?? [])];
  const headerKindLabel = [...assignedKinds]
    .sort((a, b) => {
      const ai = kindsCatalog?.findIndex((k) => k.slug === a) ?? -1;
      const bi = kindsCatalog?.findIndex((k) => k.slug === b) ?? -1;
      const ar = ai < 0 ? Number.MAX_SAFE_INTEGER : ai;
      const br = bi < 0 ? Number.MAX_SAFE_INTEGER : bi;
      return ar - br || a.localeCompare(b);
    })
    .map((slug) => {
      const fromCatalog = kindsCatalog?.find((k) => k.slug === slug)?.label;
      if (fromCatalog) return fromCatalog;
      if (slug === item.kind) return config.label;
      return slug;
    })
    .join(' + ');

  const newPoAction = canWrite(role, 'procurement') ? <NewPoButton item={item} /> : null;

  return (
    <div className="space-y-6">
      <BackLink />
      <PageHeader
        title={item.name}
        meta={
          <>
            <span className="font-mono text-xs">{item.sku}</span>
            {' · '}
            {headerKindLabel}
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            {canWrite(role, 'inventory') && (
              <Button variant="outline" size="sm" onClick={() => setDuplicateOpen(true)}>
                <Copy className="h-4 w-4" />
                Duplicate
              </Button>
            )}
            <HeaderStatusBadge item={item} />
          </div>
        }
      />

      <ArchivedStockBanner item={item} />

      <DuplicateItemDialog open={duplicateOpen} onOpenChange={setDuplicateOpen} source={item} />

      <Tabs
        value={active}
        onValueChange={(v) => {
          const next = new URLSearchParams(params);
          next.set('tab', v);
          setParams(next, { replace: true });
        }}
      >
        <TabsList className="h-auto flex-wrap gap-1 bg-secondary p-1">
          {visibleTabs.map((t) => (
            <TabsTrigger key={t.id} value={t.id} className="data-[state=active]:shadow-sm">
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {config.tabs.includes('overview') && (
          <TabsContent value="overview" className="mt-6">
            <OverviewForKind item={item} />
          </TabsContent>
        )}
        {config.tabs.includes('boms') && (
          <TabsContent value="boms" className="mt-6">
            <WhereUsedTab item={item} />
          </TabsContent>
        )}
        {config.tabs.includes('suppliers') && (
          <TabsContent value="suppliers" className="mt-6">
            <SuppliersTab itemId={item.id} baseUom={item.base_uom} />
          </TabsContent>
        )}
        {config.tabs.includes('inventory') && (
          <TabsContent value="inventory" className="mt-6">
            <InventoryTab itemId={item.id} baseUom={item.base_uom} newPoAction={newPoAction} />
          </TabsContent>
        )}
        {config.tabs.includes('sales') && (
          <TabsContent value="sales" className="mt-6">
            <SalesTab itemId={item.id} />
          </TabsContent>
        )}
        {config.tabs.includes('procurement') && (
          <TabsContent value="procurement" className="mt-6">
            <ProcurementTab itemId={item.id} baseUom={item.base_uom} newPoAction={newPoAction} />
          </TabsContent>
        )}
        {config.tabs.includes('manufacturing') && (
          <TabsContent value="manufacturing" className="mt-6">
            <ManufacturingTab itemId={item.id} />
          </TabsContent>
        )}
        {allowedTabs.includes('engineering') && (
          <TabsContent value="engineering" className="mt-6">
            <ItemEngineeringTab itemId={item.id} />
          </TabsContent>
        )}
        {config.tabs.includes('documents') && (
          <TabsContent value="documents" className="mt-6">
            <DocumentsTab itemId={item.id} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// =====================================================================
// Overview tab — per-kind compositions
// =====================================================================
//
// Each kind gets its own Overview component below. They all start out
// rendering the same `DefaultOverview` so the page works for every
// kind on day one. To diverge a kind, replace its body with whatever
// arrangement of cards makes sense — the shared building blocks
// (`ItemPhotosCard`, `ItemDetailsCard`, …) live further down so they
// can be mixed and matched freely.

/**
 * Raw material (slug `raw_stock`): raw, unprocessed stock — plastic
 * pellets, steel / aluminum stock, lumber, resin — that gets machined,
 * moulded, or cut into a part. Future work: lead with on-hand /
 * reorder-point status and a preferred-supplier / lead-time card; the
 * retail-price rows rarely apply to raw stock.
 */
function RawStockOverview({ item }: { item: Item }) {
  return <DefaultOverview item={item} />;
}

/**
 * The `raw_material` slug is labelled "Components" (migration 51):
 * part-level items, either purchased from a vendor or built in-house
 * (sub-assemblies included), that get assembled into finished goods —
 * and that may also be sold directly as replacement parts. Future
 * work: emphasize supplier/reorder data; consider a "preferred
 * supplier" / lead-time card.
 */
function RawMaterialOverview({ item }: { item: Item }) {
  return <DefaultOverview item={item} />;
}

/**
 * Finished goods are what the customer actually sees. Future work:
 * lead with the product photo, add a catalog/marketing copy card, and
 * surface live sales velocity here so listing decisions live next to
 * the master record.
 */
function FinishedGoodOverview({ item }: { item: Item }) {
  return <DefaultOverview item={item} />;
}

/**
 * Photos plus the item master split into themed cards — the baseline
 * that works for any item kind. The Finished Goods card (selling
 * prices, dealer tiers, storefront links) only renders when the item
 * is filed as a finished good, as its primary or an additional type.
 */
function DefaultOverview({ item }: { item: Item }) {
  const canSeeEngineering = useCanSeeEngineering();
  const isFinishedGood = [item.kind, ...(item.additional_kinds ?? [])].includes('finished_good');
  return (
    <div className="space-y-6">
      {/* "Where was this part engineered?" — only renders when a project is linked. */}
      {canSeeEngineering && <ItemEngineeringQuickLink itemId={item.id} />}
      <div className="grid gap-6 lg:grid-cols-3">
        <ItemPhotosCard itemId={item.id} primaryPath={item.image_path} />
        <ItemDetailsCard item={item} className="lg:col-span-2" />
      </div>
      {isFinishedGood && <FinishedGoodsCard item={item} />}
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <ItemCostingCard item={item} />
          <ItemTrackingCard item={item} />
        </div>
        <ItemReplenishmentCard item={item} />
      </div>
      <ItemDescriptionCard item={item} />
    </div>
  );
}

// =====================================================================
// Overview building blocks
// =====================================================================
//
// Each card is a self-contained Card with its own header/body. They
// take the full `Item` record so that kind-specific Overview
// components can pick which ones to render and in what order without
// re-deriving prop shapes. Every row is click-to-edit for admins (gated
// by RLS — `items_write_admin`) and a static read-out for everyone else.

/** Edit gate + single-field save shared by every Overview card. */
function useItemFieldEditor(item: Item) {
  const { role } = useAuth();
  const canEdit = canWrite(role as Role | null, 'inventory');
  const updateItem = useUpdateItem();
  const save = async (patch: UpdateItemPatch) => {
    await updateItem.mutateAsync({ id: item.id, patch });
  };
  return { canEdit, save };
}

function OverviewSection({
  title,
  className,
  contentClassName,
  children,
}: {
  title: string;
  className?: string;
  contentClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className={cn('grid grid-cols-1 gap-x-6 gap-y-3 text-sm', contentClassName)}>
        {children}
      </CardContent>
    </Card>
  );
}

function SectionSubheading({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{children}</p>
  );
}

/**
 * Identity and classification: what the item is, how it's counted, and
 * where it's filed.
 *
 * Renaming the SKU here propagates to every joined view automatically:
 * downstream tables (BOMs, POs, SOs, work orders, inventory levels,
 * stock movements, suppliers) FK to `items.id` (uuid PK). The mutation
 * also busts the React Query caches that join to `items` so any grid
 * already on screen reflects the new SKU/name on its next render.
 */
function ItemDetailsCard({ item, className }: { item: Item; className?: string }) {
  const { canEdit, save } = useItemFieldEditor(item);
  const { data: suppliers } = useItemSuppliers(item.id);
  const stockedUnit = mainPurchaseUnit(suppliers);
  // Pull peer items so the Color editor can autocomplete from the
  // organisation's existing vocabulary ("Black", "Olive", "Sage") — the
  // same list the items grid shows — instead of letting typo variants
  // sprawl.
  const { data: peers } = useItems();
  const colorOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const p of peers ?? []) {
      if (p.color) seen.add(p.color);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [peers]);

  return (
    <OverviewSection title="Item details" className={className} contentClassName="sm:grid-cols-2">
      <EditableKV
        label="SKU"
        editor={
          <EditableText
            value={item.sku}
            variant="mono"
            required
            maxLength={64}
            ariaLabel="SKU"
            canEdit={canEdit}
            density="kv"
            align="right"
            onSave={(next) => save({ sku: next })}
          />
        }
      />
      <EditableKV
        label="Parent SKU"
        editor={
          <EditableText
            value={item.parent_sku ?? ''}
            variant="mono"
            maxLength={64}
            ariaLabel="Parent SKU"
            canEdit={canEdit}
            density="kv"
            align="right"
            placeholder="—"
            onSave={(next) => save({ parent_sku: next })}
          />
        }
      />
      <EditableKV
        label="Name"
        editor={
          <EditableText
            value={item.name}
            required
            maxLength={200}
            ariaLabel="Name"
            canEdit={canEdit}
            density="kv"
            align="right"
            onSave={(next) => save({ name: next })}
          />
        }
      />
      <EditableKV
        label="Type"
        editor={
          <KindPicker
            value={[item.kind, ...(item.additional_kinds ?? [])]}
            preferredPrimary={item.kind}
            canEdit={canEdit}
            density="kv"
            align="right"
            onSave={(next) => save({ kind: next.kind, additional_kinds: next.additional_kinds })}
          />
        }
      />
      <EditableKV
        label="UoM Produced"
        editor={
          <EditableSelect
            value={item.base_uom}
            options={uomChoices(item.base_uom)}
            ariaLabel="UoM Produced"
            canEdit={canEdit}
            density="kv"
            align="right"
            onSave={(next) => save({ base_uom: next })}
          />
        }
      />
      <KV
        label="UoM Stocked"
        value={
          stockedUnit ? (
            <span className="tabular-nums">
              {stockedUnit.purchaseUom}
              <span className="block text-xs text-muted-foreground">
                {describePurchaseUnit(stockedUnit, item.base_uom)}
              </span>
            </span>
          ) : (
            <span>
              {item.base_uom}
              <span className="block text-xs text-muted-foreground">
                Same as produced. Set a roll, box… on the supplier.
              </span>
            </span>
          )
        }
      />
      <EditableKV
        label="Status"
        editor={<ItemStatusControl item={item} canEdit={canEdit} density="kv" align="right" />}
      />
      <EditableKV
        label="Color"
        editor={
          <EditableCategory
            value={item.color}
            options={colorOptions}
            ariaLabel="Color"
            canEdit={canEdit}
            density="kv"
            align="right"
            placeholder="No color"
            onSave={(next) => save({ color: next })}
          />
        }
      />
      <EditableKV
        label="Region / market"
        editor={
          <EditableText
            value={item.region ?? ''}
            maxLength={32}
            ariaLabel="Region / market"
            canEdit={canEdit}
            density="kv"
            align="right"
            placeholder="e.g. US, EU, AU/NZ"
            onSave={(next) => save({ region: next })}
          />
        }
      />
      <EditableKV
        label="Model filter"
        editor={
          <EditableText
            value={(item.model_filters ?? []).join(', ')}
            maxLength={200}
            ariaLabel="Model filter"
            canEdit={canEdit}
            density="kv"
            align="right"
            placeholder="e.g. Manual, Electric"
            onSave={(next) =>
              save({
                model_filters: Array.from(
                  new Set(next.split(',').map((s) => s.trim()).filter(Boolean)),
                ),
              })
            }
          />
        }
      />
      <KV
        label="Created"
        value={<span className="text-muted-foreground">{formatDate(item.created_at)}</span>}
      />
      <div className="sm:col-span-2">
        <p className="text-xs font-medium text-muted-foreground">Tags</p>
        <div className="mt-1.5">
          <TagsPicker itemId={item.id} canEdit={canEdit} />
        </div>
      </div>
    </OverviewSection>
  );
}

/**
 * Finished-good-only fields: what we sell it for (retail and dealer
 * tiers, with margin/markup derived from the live values so they
 * update as the inputs commit) and where it's listed online.
 */
function FinishedGoodsCard({ item }: { item: Item }) {
  const { canEdit, save } = useItemFieldEditor(item);
  const costCents = useItemUnitCost(item.id).cents;

  const margin =
    item.list_price_cents != null && costCents != null && item.list_price_cents > 0
      ? (item.list_price_cents - costCents) / item.list_price_cents
      : null;

  return (
    <OverviewSection title="Finished Goods" contentClassName="gap-y-6 lg:grid-cols-3">
      <div className="space-y-3">
        <SectionSubheading>Retail</SectionSubheading>
        <EditableKV
          label="Retail price"
          editor={
            <EditableCurrency
              value={item.list_price_cents}
              ariaLabel="Retail price"
              canEdit={canEdit}
              density="kv"
              align="right"
              onSave={(next) => save({ list_price_cents: next })}
            />
          }
        />
        <KV
          label="Gross margin"
          value={
            margin != null ? (
              <span className="tabular-nums text-bright-moss-dark">
                {(margin * 100).toFixed(1)}%
              </span>
            ) : (
              '—'
            )
          }
        />
        <KV
          label="Markup"
          value={
            costCents != null && item.list_price_cents != null && costCents > 0 ? (
              <span className="tabular-nums">
                {(item.list_price_cents / costCents).toFixed(2)}×
              </span>
            ) : (
              '—'
            )
          }
        />
      </div>
      <div className="space-y-3">
        <SectionSubheading>Dealer</SectionSubheading>
        {(
          [
            ['dealer_price_4_cents', 'Dealer price (4 unit MOQ)'],
            ['dealer_price_18_cents', 'Dealer price (18 unit MOQ)'],
            ['dealer_price_180_cents', 'Dealer price (180 unit MOQ)'],
          ] as const
        ).map(([key, label]) => (
          <EditableKV
            key={key}
            label={label}
            editor={
              <EditableCurrency
                value={item[key]}
                ariaLabel={label}
                canEdit={canEdit}
                density="kv"
                align="right"
                onSave={(next) => save({ [key]: next })}
              />
            }
          />
        ))}
        <EditableKV
          label="Dealer MOQ"
          editor={
            <EditableNumber
              value={item.dealer_moq}
              ariaLabel="Dealer MOQ"
              canEdit={canEdit}
              density="kv"
              align="right"
              onSave={(next) => save({ dealer_moq: next })}
            />
          }
        />
      </div>
      <div className="space-y-3">
        <SectionSubheading>Virro listings</SectionSubheading>
        <ListingLinkKV
          label="Shopify link"
          value={item.shopify_url}
          canEdit={canEdit}
          placeholder="Paste the Shopify product URL"
          onSave={(next) => save({ shopify_url: next })}
        />
        <ListingLinkKV
          label="Amazon link"
          value={item.amazon_url}
          canEdit={canEdit}
          placeholder="Paste the Amazon listing URL"
          onSave={(next) => save({ amazon_url: next })}
        />
      </div>
    </OverviewSection>
  );
}

/**
 * A storefront link row: click-to-edit for admins with an "open" button
 * beside it, a plain external link for everyone else.
 */
function ListingLinkKV({
  label,
  value,
  canEdit,
  placeholder,
  onSave,
}: {
  label: string;
  value: string | null;
  canEdit: boolean;
  placeholder: string;
  onSave: (next: string) => Promise<void>;
}) {
  const shortUrl = value?.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');
  return (
    <EditableKV
      label={label}
      editor={
        canEdit ? (
          <div className="flex min-w-0 items-center justify-end gap-1">
            <div className="min-w-0 flex-1">
              <EditableText
                value={value ?? ''}
                ariaLabel={label}
                canEdit
                density="kv"
                align="right"
                placeholder={placeholder}
                maxLength={2048}
                display={shortUrl ? <span title={value ?? undefined}>{shortUrl}</span> : undefined}
                onSave={onSave}
              />
            </div>
            {value && (
              <Button asChild variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                <a
                  href={value}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${label}`}
                  title={`Open ${label}`}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </Button>
            )}
          </div>
        ) : value ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            title={value}
            className="inline-flex max-w-full items-center justify-end gap-1 text-foreground hover:underline"
          >
            <span className="truncate">{shortUrl}</span>
            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      }
    />
  );
}

/**
 * Who we buy this item from and their part number for it. Uses the primary
 * supplier, else the first on file; `useItemSuppliers` sorts primary first.
 */
function SupplierInfoRows({ suppliers }: { suppliers: ItemSupplier[] | undefined }) {
  const row = suppliers?.[0];
  if (!row) return null;
  const others = suppliers.length - 1;
  const website = websiteHref(row.vendors?.website);
  return (
    <>
      <KV
        label="Supplier name"
        value={
          <span>
            {row.vendors?.name ?? '—'}
            {website && (
              <a
                href={website}
                target="_blank"
                rel="noreferrer"
                className="ml-1.5 inline-flex items-center text-muted-foreground hover:text-foreground"
                aria-label="Open supplier website"
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
            {others > 0 && (
              <Link
                to="?tab=suppliers"
                className="block text-xs text-muted-foreground hover:underline"
              >
                +{others} more {others === 1 ? 'supplier' : 'suppliers'}
              </Link>
            )}
          </span>
        }
      />
      <KV
        label="Supplier SKU"
        value={<span className="font-mono text-xs">{row.supplier_part_number?.trim() || '—'}</span>}
      />
    </>
  );
}

/**
 * The supplier price as it's ordered ("1 roll = $224.00") and the same
 * price per stock unit. Uses the primary supplier's price, else the first
 * supplier with one; `useItemSuppliers` already sorts primary first.
 */
function SupplierUnitCostRows({
  suppliers,
  baseUom,
}: {
  suppliers: ItemSupplier[] | undefined;
  baseUom: string;
}) {
  const row = suppliers?.find((s) => s.unit_cost_cents != null);
  if (!row || row.unit_cost_cents == null) return null;
  const unit = supplierPurchaseUnit(row);
  const conv = unit?.unitsPerPurchaseUom ?? 1;
  const packed = !!unit && isPackUnit(conv);
  const buyUom = packed ? unit.purchaseUom! : baseUom;
  const source = [
    row.vendors?.name,
    row.unit_cost_as_of
      ? `${row.unit_cost_po_line_id ? 'PO price' : 'Quoted'} ${formatDateOnly(row.unit_cost_as_of)}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <>
      <KV
        label={`Unit cost (per ${buyUom})`}
        value={
          <span className="tabular-nums">
            1 {buyUom} = {formatUnitCurrency(row.unit_cost_cents)}
            {source && <span className="block text-xs text-muted-foreground">{source}</span>}
          </span>
        }
      />
    </>
  );
}

/**
 * What one UoM Produced costs: the BOM roll-up for a built item, else the
 * landed cost on the latest PO, else the supplier price ÷ units per
 * purchase unit (migration 162).
 */
function useItemUnitCost(itemId: string) {
  const { data: current } = useItemCurrentCost(itemId);
  const { data: graph } = useBomGraph();
  const rolled = useMemo(
    () => (hasBomLines(graph, itemId) ? makeCostResolver(graph)(itemId) : null),
    [graph, itemId],
  );
  if (rolled) return { cents: rolled.missing ? null : rolled.cents, rolled, current: null };
  return { cents: current?.unit_cost_cents ?? null, rolled: null, current: current ?? null };
}

function ItemUnitCostRow({
  baseUom,
  cost,
}: {
  baseUom: string;
  cost: ReturnType<typeof useItemUnitCost>;
}) {
  const { rolled, current } = cost;
  let value: React.ReactNode = (
    <span className="text-muted-foreground">
      —<span className="block text-xs">Add a supplier price or a PO to cost it.</span>
    </span>
  );
  if (rolled) {
    value = (
      <span className="tabular-nums">
        {formatUnitCostCents(rolled.cents)}
        <span className="ml-1 text-xs text-muted-foreground">/{baseUom}</span>
        <span className="block text-xs text-muted-foreground">
          Rolled up from the BOM
          {rolled.missing > 0 &&
            ` · ${rolled.missing} part${rolled.missing === 1 ? '' : 's'} without a cost`}
        </span>
      </span>
    );
  } else if (current) {
    value = (
      <span className="tabular-nums">
        {formatUnitCostCents(current.unit_cost_cents)}
        <span className="ml-1 text-xs text-muted-foreground">/{baseUom}</span>
        <span className="block text-xs text-muted-foreground">
          {itemCostSourceLabel(current)}
          {current.as_of && ` · ${formatDateOnly(current.as_of)}`}
        </span>
        {current.cost_source === 'po' && (
          <LandedCostBreakdown
            always
            parts={{
              goods: current.goods_cents ?? 0,
              tariff: current.tariff_cents ?? 0,
              shipping: current.shipping_cents ?? 0,
              other: current.other_charges_cents ?? 0,
              landed: current.unit_cost_cents,
            }}
          />
        )}
      </span>
    );
  }
  return <KV label={`Cost per ${baseUom}`} value={value} />;
}

/** What the item costs us: supplier price, cost per unit, and landed cost history. */
function ItemCostingCard({ item }: { item: Item }) {
  const unitCost = useItemUnitCost(item.id);
  const { data: costSummary } = useItemCostSummary(item.id);
  const { data: suppliers } = useItemSuppliers(item.id);

  return (
    <OverviewSection title="Costing">
      <SupplierUnitCostRows suppliers={suppliers} baseUom={item.base_uom} />
      <ItemUnitCostRow baseUom={item.base_uom} cost={unitCost} />
      <KV
        label="Last landed cost"
        value={
          costSummary?.last_cost_cents != null ? (
            <span className="tabular-nums">
              {formatUnitCostCents(costSummary.last_cost_cents_exact ?? costSummary.last_cost_cents)}
              <span className="ml-1 text-xs text-muted-foreground">/{item.base_uom}</span>
              <span className="ml-1 text-xs text-muted-foreground">
                {formatDate(costSummary.last_received_at)}
              </span>
              <LandedCostBreakdown parts={lastLandedCostParts(costSummary)} />
            </span>
          ) : (
            '—'
          )
        }
      />
      <KV
        label="Avg landed cost (12 mo)"
        value={
          costSummary?.avg_cost_12m_cents != null ? (
            <span className="tabular-nums">
              {formatUnitCostCents(costSummary.avg_cost_12m_cents_exact ?? costSummary.avg_cost_12m_cents)}
              <span className="ml-1 text-xs text-muted-foreground">/{item.base_uom}</span>
              <LandedCostBreakdown parts={avgLandedCostParts(costSummary)} />
            </span>
          ) : (
            '—'
          )
        }
      />
    </OverviewSection>
  );
}

const NO_WORK_CENTER = '__none__';

/** Make or buy, who from, how long it takes to get, and when to reorder. */
function ItemReplenishmentCard({ item }: { item: Item }) {
  const { canEdit, save } = useItemFieldEditor(item);
  const { data: workResources } = useWorkResources();
  const { data: suppliers } = useItemSuppliers(item.id);
  const stockedUnit = mainPurchaseUnit(suppliers);
  const defaultOverstock = defaultOverstockAt(item.reorder_point, item.reorder_qty);

  return (
    <OverviewSection title="Sourcing & replenishment">
      <EditableKV
        label="Made in-house"
        editor={
          <EditableBool
            value={item.is_made_in_house ?? false}
            ariaLabel="Made in-house"
            canEdit={canEdit}
            density="kv"
            align="right"
            trueLabel="Yes"
            falseLabel="No — purchased"
            onSave={(next) => save({ is_made_in_house: next })}
          />
        }
      />
      <SupplierInfoRows suppliers={suppliers} />
      {!item.is_made_in_house && (
        <PurchaseLinkRow
          url={item.purchase_url}
          canEdit={canEdit}
          onSave={(next) => save({ purchase_url: next })}
        />
      )}
      {item.is_made_in_house && (
        <EditableKV
          label="Made on"
          editor={
            <EditableSelect<string>
              value={item.default_work_center_id ?? NO_WORK_CENTER}
              ariaLabel="Machine that makes this item"
              canEdit={canEdit}
              density="kv"
              align="right"
              options={[
                { value: NO_WORK_CENTER, label: 'Not set' },
                ...(workResources ?? [])
                  .filter((r) => r.is_active || r.id === item.default_work_center_id)
                  .map((r) => ({ value: r.id, label: `${r.code} · ${r.name}` })),
              ]}
              onSave={(next) =>
                save({ default_work_center_id: next === NO_WORK_CENTER ? null : next })
              }
            />
          }
        />
      )}
      <EditableKV
        label="Lead time"
        editor={
          <EditableText
            value={item.lead_time_text ?? ''}
            ariaLabel="Lead time"
            canEdit={canEdit}
            density="kv"
            align="right"
            placeholder="e.g. 2 days, or unknown — expect 2-4 weeks"
            maxLength={200}
            onSave={(next) => save({ lead_time_text: next })}
          />
        }
      />
      <EditableKV
        label="Worst-case lead (days)"
        editor={
          <EditableNumber
            value={item.lead_time_worst_case_days}
            ariaLabel="Worst-case lead time in days"
            canEdit={canEdit}
            density="kv"
            align="right"
            onSave={(next) => save({ lead_time_worst_case_days: next })}
          />
        }
      />
      <ReorderQtyRow
        label="Reorder point"
        value={item.reorder_point}
        baseUom={item.base_uom}
        stockedUnit={stockedUnit}
        canEdit={canEdit}
        onSave={(next) => save({ reorder_point: next })}
      />
      <ReorderQtyRow
        label="Reorder quantity"
        value={item.reorder_qty}
        baseUom={item.base_uom}
        stockedUnit={stockedUnit}
        canEdit={canEdit}
        onSave={(next) => save({ reorder_qty: next })}
      />
      <ReorderQtyRow
        label="Overstock at"
        value={item.overstock_at ?? null}
        baseUom={item.base_uom}
        stockedUnit={stockedUnit}
        canEdit={canEdit}
        onSave={(next) => save({ overstock_at: next })}
        emptyDisplay={<span className="text-muted-foreground">Default</span>}
        hint={
          <>
            Optional override — leave blank to use the default. Default is reorder point + reorder
            quantity
            {defaultOverstock == null
              ? '; set both to get one.'
              : ` = ${formatReorderQty(defaultOverstock, item.base_uom, stockedUnit)}.`}
          </>
        }
      />
      {item.is_made_in_house && (
        <p className="text-xs text-muted-foreground">
          {item.reorder_point == null
            ? 'Set a reorder point to have draft production orders created automatically.'
            : `When on hand + on order falls to ${formatQtyUom(item.reorder_point, item.base_uom)} or below, a draft production order for at least ${formatQtyUom(
                Math.max(1, item.reorder_qty || item.reorder_point),
                item.base_uom,
              )} is created on Work schedules for review.`}
        </p>
      )}
    </OverviewSection>
  );
}

/**
 * Reorder point / quantity are stored in the UoM Produced, which is what
 * on-hand and every reorder check count in, but people think in the UoM
 * Stocked: 2 rolls, not 3,600 in. Edit in the stocked unit when there is one.
 */
function reorderUnits(baseUom: string, stockedUnit: PurchaseUnit | null) {
  const conv = stockedUnit?.unitsPerPurchaseUom ?? 1;
  const stockedUom = stockedUnit?.purchaseUom?.trim();
  const inStocked = !!stockedUom && isPackUnit(conv);
  return {
    inStocked,
    conv,
    uom: inStocked ? stockedUom : baseUom,
    unitLabel: inStocked ? pluralUom(stockedUom, 2) : baseUom,
    toShown: (value: number) => (inStocked ? Math.round((value / conv) * 1000) / 1000 : value),
  };
}

/** A UoM Produced quantity in the unit the reorder rows edit in, e.g. "2 rolls (3,600 in)". */
function formatReorderQty(value: number, baseUom: string, stockedUnit: PurchaseUnit | null): string {
  const units = reorderUnits(baseUom, stockedUnit);
  const shown = formatQtyUom(units.toShown(value), units.uom);
  return units.inStocked ? `${shown} (${formatQtyUom(value, baseUom)})` : shown;
}

function ReorderQtyRow({
  label,
  value,
  baseUom,
  stockedUnit,
  canEdit,
  onSave,
  emptyDisplay,
  hint,
}: {
  label: string;
  value: number | null;
  baseUom: string;
  stockedUnit: PurchaseUnit | null;
  canEdit: boolean;
  onSave: (next: number | null) => Promise<void> | void;
  /** Shown in place of "—" when the value is blank. */
  emptyDisplay?: React.ReactNode;
  hint?: React.ReactNode;
}) {
  const { inStocked, conv, uom, unitLabel, toShown } = reorderUnits(baseUom, stockedUnit);
  const shown = value == null ? null : toShown(value);
  const row = (
    <EditableKV
      label={`${label} (${unitLabel})`}
      editor={
        <EditableNumber
          value={shown}
          ariaLabel={`${label} in ${unitLabel}`}
          canEdit={canEdit}
          density="kv"
          align="right"
          allowDecimal={inStocked}
          display={
            value == null ? (
              emptyDisplay
            ) : (
              <span>
                {formatQtyUom(shown ?? 0, uom)}
                {inStocked && (
                  <span className="block text-xs text-muted-foreground">
                    = {formatQtyUom(value, baseUom)}
                  </span>
                )}
              </span>
            )
          }
          onSave={(next) => onSave(next == null ? null : inStocked ? Math.round(next * conv) : next)}
        />
      }
    />
  );
  if (!hint) return row;
  return (
    <div className="space-y-1">
      {row}
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

/** Lot / serial capture and the Work Mode stations the item is worked on. */
function ItemTrackingCard({ item }: { item: Item }) {
  const { canEdit, save } = useItemFieldEditor(item);

  return (
    <OverviewSection title="Tracking & workstations">
      <EditableKV
        label="Lot tracked"
        editor={
          <EditableBool
            value={item.is_lot_tracked}
            ariaLabel="Lot tracked"
            canEdit={canEdit}
            density="kv"
            align="right"
            onSave={(next) => save({ is_lot_tracked: next })}
          />
        }
      />
      <EditableKV
        label="Serial tracked"
        editor={
          <EditableBool
            value={item.is_serialized ?? false}
            ariaLabel="Serial tracked"
            canEdit={canEdit}
            density="kv"
            align="right"
            onSave={(next) => save({ is_serialized: next })}
          />
        }
      />
      <div>
        <p className="text-xs font-medium text-muted-foreground">Workstations</p>
        <div className="mt-1.5">
          <WorkstationsPicker itemId={item.id} canEdit={canEdit} />
        </div>
      </div>
    </OverviewSection>
  );
}

/** Long-form text. Hidden for read-only viewers when both fields are empty. */
function ItemDescriptionCard({ item }: { item: Item }) {
  const { canEdit, save } = useItemFieldEditor(item);
  const showDescription = canEdit || !!item.description;
  const showNotes = canEdit || !!item.notes;
  if (!showDescription && !showNotes) return null;

  return (
    <OverviewSection
      title={showDescription && showNotes ? 'Description & notes' : showNotes ? 'Notes' : 'Description'}
      contentClassName={cn('gap-y-6', showDescription && showNotes && 'lg:grid-cols-2')}
    >
      {showDescription && (
        <div>
          <p className="text-xs font-medium text-muted-foreground">Description</p>
          <div className="mt-1">
            <EditableText
              value={item.description ?? ''}
              ariaLabel="Description"
              canEdit={canEdit}
              density="kv"
              multiline
              rows={8}
              placeholder="Add a short description…"
              maxLength={2000}
              triggerClassName="text-sm"
              inputClassName="min-h-[12rem]"
              onSave={(next) => save({ description: next })}
            />
          </div>
        </div>
      )}
      {showNotes && (
        <div>
          <p className="text-xs font-medium text-muted-foreground">Notes</p>
          <div className="mt-1">
            <EditableText
              value={item.notes ?? ''}
              ariaLabel="Notes"
              canEdit={canEdit}
              density="kv"
              multiline
              rows={4}
              placeholder="Add general comments…"
              maxLength={2000}
              triggerClassName="text-sm"
              inputClassName="min-h-[6rem]"
              onSave={(next) => save({ notes: next })}
            />
          </div>
        </div>
      )}
    </OverviewSection>
  );
}

/**
 * Quick link to the web page a purchased item is bought from. The open
 * button sits beside the inline editor (not inside it) so clicking the
 * link never drops the row into edit mode.
 */
function PurchaseLinkRow({
  url,
  canEdit,
  onSave,
}: {
  url: string | null;
  canEdit: boolean;
  onSave: (next: string) => Promise<void>;
}) {
  const href = websiteHref(url);
  return (
    <EditableKV
      label="Purchase link"
      editor={
        <div className="flex items-center justify-end gap-2">
          <div className="min-w-0 flex-1">
            <EditableText
              value={url ?? ''}
              ariaLabel="Purchase link"
              canEdit={canEdit}
              density="kv"
              align="right"
              placeholder={canEdit ? 'Add website link' : '—'}
              maxLength={2000}
              display={
                url ? <span className="block truncate">{websiteLabel(url)}</span> : undefined
              }
              onSave={onSave}
            />
          </div>
          {href && (
            <Button asChild variant="outline" size="sm" className="h-7 shrink-0 px-2">
              <a href={href} target="_blank" rel="noreferrer" aria-label="Open purchase link">
                <ExternalLink className="h-3.5 w-3.5" />
                Open
              </a>
            </Button>
          )}
        </div>
      }
    />
  );
}

function WhereUsedTab({ item }: { item: Item }) {
  const itemId = item.id;
  const { role } = useAuth();
  const canEdit = canWrite(role as Role | null, 'inventory');
  const [bomOpen, setBomOpen] = useState(false);
  const [bomMode, setBomMode] = useState<AddToBomMode>('add-to-parent');

  // Both BOM cards share one dialog instance — the card that launched
  // it sets the direction. Keeping it mounted (rather than mounting per
  // click) lets the close animation play out.
  const openBomDialog = (mode: AddToBomMode) => {
    setBomMode(mode);
    setBomOpen(true);
  };

  const { data: bom, isLoading: bomLoading } = useBomForItem(itemId);
  const { data: containedBy, isLoading: cbLoading } = useBomsContainingItem(itemId);

  // Walk to the active version's lines so the BOM table renders the
  // currently-shipping recipe rather than a historical one.
  const activeVersion = useMemo(() => {
    if (!bom) return null;
    return bom.bom_versions.find((v) => v.is_active) ?? bom.bom_versions[0] ?? null;
  }, [bom]);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1.5">
              <CardTitle>This item is built from</CardTitle>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button asChild type="button" variant="ghost" size="sm">
                <Link to={`/mfg/boms/${itemId}`}>
                  <Layers className="h-4 w-4" />
                  Open BOM builder
                </Link>
              </Button>
              {canEdit && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => openBomDialog('add-component')}
                >
                  <Plus className="h-4 w-4" />
                  Add component
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {bomLoading ? (
            <SkeletonRows />
          ) : !bom || !activeVersion || activeVersion.bom_lines.length === 0 ? (
            <EmptyState
              icon={Layers}
              title="No active BOM"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Component</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>UoM</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeVersion.bom_lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell className="font-mono text-xs">{line.items?.sku ?? '—'}</TableCell>
                    <TableCell>
                      {line.items ? (
                        <Link
                          to={`/catalog/items/${line.items.id}`}
                          className="font-medium text-foreground hover:underline"
                        >
                          {line.items.name}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Number(line.quantity).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{line.items?.base_uom || line.uom}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1.5">
              <CardTitle>Used in (parent assemblies)</CardTitle>
            </div>
            {canEdit && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => openBomDialog('add-to-parent')}
              >
                <Plus className="h-4 w-4" />
                Add to BOM
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {cbLoading ? (
            <SkeletonRows />
          ) : !containedBy || containedBy.length === 0 ? (
            <EmptyState
              icon={Layers}
              title="Not used in any BOM"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Assembly SKU</TableHead>
                  <TableHead>Assembly name</TableHead>
                  <TableHead className="text-right">Per unit</TableHead>
                  <TableHead>Version</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {containedBy.map((row) => {
                  const parent = row.bom_versions?.boms?.items;
                  const ver = row.bom_versions;
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">{parent?.sku ?? '—'}</TableCell>
                      <TableCell>
                        {parent ? (
                          <Link
                            to={`/catalog/items/${parent.id}`}
                            className="font-medium text-foreground hover:underline"
                          >
                            {parent.name}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {Number(row.quantity).toLocaleString()} {item.base_uom}
                      </TableCell>
                      <TableCell>
                        {ver ? (
                          <Badge variant={ver.is_active ? 'success' : 'muted'}>
                            v{ver.version}
                            {ver.is_active ? ' · active' : ''}
                          </Badge>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {canEdit && (
        <AddToBomDialog open={bomOpen} onOpenChange={setBomOpen} item={item} mode={bomMode} />
      )}
    </div>
  );
}

function SuppliersTab({ itemId, baseUom }: { itemId: string; baseUom: string }) {
  const { role } = useAuth();
  const canEdit = canWrite(role as Role | null, 'inventory');
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<ItemSupplier | null>(null);
  const { data, isLoading } = useItemSuppliers(itemId);

  const hasPrimary = useMemo(() => (data ?? []).some((s) => s.is_primary), [data]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1.5">
            <CardTitle>Suppliers</CardTitle>
          </div>
          {canEdit && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => setAddOpen(true)}
            >
              <Plus className="h-4 w-4" />
              Add supplier
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonRows />
        ) : !data || data.length === 0 ? (
          <EmptyState
            icon={Factory}
            title="No suppliers on file"
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendor</TableHead>
                <TableHead>Part #</TableHead>
                <TableHead>UoM Stocked</TableHead>
                <TableHead className="text-right">Unit cost</TableHead>
                <TableHead className="text-right">Lead time</TableHead>
                <TableHead className="text-right">MOQ</TableHead>
                <TableHead className="text-right">Multiple</TableHead>
                <TableHead>Price breaks</TableHead>
                <TableHead>Primary</TableHead>
                {canEdit && <TableHead className="w-10" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row) => {
                const unit = supplierPurchaseUnit(row);
                const conv = unit?.unitsPerPurchaseUom ?? 1;
                const packed = !!unit && isPackUnit(conv);
                const buyUom = packed ? unit.purchaseUom! : baseUom;
                const pricing = packed ? supplierPricingUnit(row) : null;
                return (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">
                    {row.vendors?.name ?? '—'}
                    {row.vendors?.website && (
                      <a
                        href={row.vendors.website}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-1.5 inline-flex items-center text-muted-foreground hover:text-foreground"
                        aria-label="Open vendor website"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {row.supplier_part_number ?? '—'}
                  </TableCell>
                  <TableCell className="text-xs tabular-nums">
                    {packed ? (
                      <>
                        {describePurchaseUnit(unit, baseUom)}
                        {pricing && (
                          <span className="block text-muted-foreground">
                            {describePricingUnit(pricing, buyUom)}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-muted-foreground">{baseUom}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.unit_cost_cents != null ? (
                      <>
                        {pricing ? (
                          <>
                            {formatUnitCurrency(toPriceUomCents(row.unit_cost_cents, pricing))}
                            <span className="text-xs text-muted-foreground">/{pricing.priceUom}</span>
                            <span className="block text-xs text-muted-foreground">
                              {formatUnitCurrency(row.unit_cost_cents)}/{buyUom}
                            </span>
                          </>
                        ) : (
                          <>
                            {formatUnitCurrency(row.unit_cost_cents)}
                            <span className="text-xs text-muted-foreground">/{buyUom}</span>
                          </>
                        )}
                        {packed && (
                          <span className="block text-xs text-muted-foreground">
                            {formatUnitCostCents(row.unit_cost_cents / conv)}/{baseUom}
                          </span>
                        )}
                        {row.unit_cost_as_of && (
                          <span
                            className="block text-xs text-muted-foreground"
                            title={
                              row.unit_cost_po_line_id
                                ? 'Taken from the most recent purchase order for this item and vendor.'
                                : 'Entered by hand.'
                            }
                          >
                            {row.unit_cost_po_line_id ? 'PO price' : 'Quoted'} ·{' '}
                            {formatDateOnly(row.unit_cost_as_of)}
                          </span>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.lead_time_days != null ? `${row.lead_time_days}d` : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.min_order_qty != null ? formatQtyUom(Number(row.min_order_qty), buyUom) : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.order_multiple != null
                      ? `×${formatQtyUom(Number(row.order_multiple), buyUom)}`
                      : '—'}
                  </TableCell>
                  <TableCell className="text-xs tabular-nums">
                    {row.item_supplier_price_breaks && row.item_supplier_price_breaks.length > 0
                      ? row.item_supplier_price_breaks
                          .map(
                            (b) =>
                              pricing
                                ? `${formatQtyUom(b.min_qty, buyUom)}+ ${formatUnitCurrency(toPriceUomCents(b.unit_cost_cents, pricing))}/${pricing.priceUom}`
                                : `${formatQtyUom(b.min_qty, buyUom)}+ ${formatUnitCurrency(b.unit_cost_cents)}/${buyUom}`,
                          )
                          .join(' · ')
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {row.is_primary ? (
                      <Badge variant="success">Primary</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => setEditing(row)}
                        aria-label={`Edit ${row.vendors?.name ?? 'supplier'}`}
                        title="Edit supplier"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
      {canEdit && (
        <AddSupplierDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          itemId={itemId}
          hasPrimary={hasPrimary}
          baseUom={baseUom}
        />
      )}
      {canEdit && (
        <AddSupplierDialog
          open={!!editing}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          itemId={itemId}
          hasPrimary={hasPrimary}
          supplier={editing}
          baseUom={baseUom}
        />
      )}
    </Card>
  );
}

function PoNumberLink({ po }: { po: { id: string; po_number: string } | null | undefined }) {
  if (!po) return <>—</>;
  return (
    <Link
      to={`/procurement/purchase-orders?po=${po.id}`}
      className="text-foreground hover:underline"
    >
      {po.po_number}
    </Link>
  );
}

/** Live carrier ETA when tracking has one, otherwise the vendor-promised date. */
function arrivalDate(
  po: { tracking_estimated_delivery: string | null; expected_at: string | null } | null,
): string | null {
  return po?.tracking_estimated_delivery ?? po?.expected_at ?? null;
}

const PRODUCTION_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft — review',
  planned: 'Planned',
  released: 'Released',
  in_progress: 'In progress',
  blocked: 'Blocked',
};

function NewPoButton({ item }: { item: Item }) {
  // Archived items can't go on a PO, so open a blank one instead of seeding it.
  const query = item.status === 'archived' ? 'new=1' : `new=1&item=${item.id}`;
  return (
    <Button asChild size="sm">
      <Link to={`/procurement/purchase-orders?${query}`}>
        <Plus className="h-4 w-4" />
        New PO
      </Link>
    </Button>
  );
}

function InventoryTab({
  itemId,
  baseUom,
  newPoAction,
}: {
  itemId: string;
  baseUom: string;
  newPoAction?: React.ReactNode;
}) {
  const { data, isLoading } = useInventoryLevels(itemId);
  const { data: poLines, isLoading: poLoading } = useItemPurchaseOrderLines(itemId);
  const { data: productionOrders } = useItemProductionOrders(itemId);
  const { data: suppliers } = useItemSuppliers(itemId);
  const buyUnit = mainPurchaseUnit(suppliers);
  const stockQty = (qty: number) => {
    const breakdown = buyUnit
      ? breakdownInPurchaseUnits(qty, buyUnit.unitsPerPurchaseUom, buyUnit.purchaseUom!, baseUom)
      : null;
    return (
      <>
        {formatQtyUom(qty, baseUom)}
        {breakdown && qty !== 0 && (
          <span className="ml-1.5 text-xs font-normal text-muted-foreground">≈ {breakdown}</span>
        )}
      </>
    );
  };

  // In-transit is the unreceived quantity on open POs (anything not
  // delivered / cancelled). Allocations would come from a
  // `useItemAllocations` hook later — for now the allocated bucket is a
  // 0 fallback so the panel slot is visible from day one.
  const inTransitLines = useMemo(
    () =>
      (poLines ?? [])
        .map((line) => ({
          ...line,
          remainingOrdered: Math.max(0, (line.quantity ?? 0) - (line.received_quantity ?? 0)),
          remaining: Math.round(
            Math.max(0, (line.quantity ?? 0) - (line.received_quantity ?? 0)) *
              (Number(line.conversion_to_base) || 1),
          ),
        }))
        .filter(
          (line) =>
            line.remaining > 0 &&
            !!line.purchase_orders &&
            !PO_CLOSED_STATUSES.includes(line.purchase_orders.status as PoStatus),
        )
        .sort((a, b) =>
          (arrivalDate(a.purchase_orders) ?? '9999').localeCompare(
            arrivalDate(b.purchase_orders) ?? '9999',
          ),
        ),
    [poLines],
  );

  const stats = useMemo(() => {
    const onHand = (data ?? [])
      .filter((r) => r.locations?.kind !== 'transit')
      .reduce((acc, r) => acc + (r.quantity ?? 0), 0);
    const inTransit = inTransitLines.reduce((acc, line) => acc + line.remaining, 0);
    const inProduction = (productionOrders ?? [])
      .filter((o) => ON_ORDER_PRODUCTION_STATUSES.includes(o.status))
      .reduce((acc, o) => acc + Math.max(0, o.quantity - (o.actual_quantity ?? 0)), 0);
    return { onHand, inTransit, inProduction, allocated: 0 };
  }, [data, inTransitLines, productionOrders]);

  return (
    <div className="space-y-6">
      {newPoAction && <div className="flex justify-end">{newPoAction}</div>}
      <div className={cn('grid gap-4', productionOrders?.length ? 'sm:grid-cols-4' : 'sm:grid-cols-3')}>
        <SummaryCard label="On-hand" value={stockQty(stats.onHand)} />
        <SummaryCard label="In-transit" value={stockQty(stats.inTransit)} />
        {!!productionOrders?.length && (
          <SummaryCard label="In production" value={stockQty(stats.inProduction)} />
        )}
        <SummaryCard label="Allocated" value={stockQty(stats.allocated)} />
      </div>
      {buyUnit && (
        <p className="text-xs text-muted-foreground">
          UoM Stocked: {buyUnit.purchaseUom} ({describePurchaseUnit(buyUnit, baseUom)}). On-hand is
          shown in {pluralUom(baseUom, 2)}, the UoM Produced.
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>In transit</CardTitle>
        </CardHeader>
        <CardContent>
          {poLoading ? (
            <SkeletonRows />
          ) : inTransitLines.length === 0 ? (
            <EmptyState icon={Truck} title="Nothing on open purchase orders" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO #</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">In transit</TableHead>
                  <TableHead>Tracking</TableHead>
                  <TableHead>Arrival</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inTransitLines.map((line) => {
                  const po = line.purchase_orders!;
                  const status = po.status as PoStatus;
                  const shipments = [...(po.po_shipments ?? [])].sort(
                    (a, b) => a.position - b.position,
                  );
                  const arrival = arrivalDate(po);
                  return (
                    <TableRow key={line.id}>
                      <TableCell className="font-mono text-xs">
                        <PoNumberLink po={po} />
                      </TableCell>
                      <TableCell>{po.vendors?.name ?? '—'}</TableCell>
                      <TableCell>
                        <Badge variant={PO_STATUS_VARIANTS[status] ?? 'muted'}>
                          {PO_STATUS_LABELS[status] ?? po.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatQtyUom(line.remainingOrdered, line.uom || baseUom)}
                        {line.received_quantity > 0 && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            of {Number(line.quantity).toLocaleString()}
                          </span>
                        )}
                        {isPackUnit(Number(line.conversion_to_base)) && (
                          <span className="block text-xs text-muted-foreground">
                            = {formatQtyUom(line.remaining, baseUom)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {shipments.length > 0 ? (
                          <>
                            {shipments.map((s) => {
                              const trackingHref = purchaseOrderTrackingUrl(s);
                              return (
                                <div key={s.id} title={s.description ?? undefined}>
                                  {s.carrier && (
                                    <span className="mr-1 text-muted-foreground">{s.carrier}</span>
                                  )}
                                  {trackingHref ? (
                                    <a
                                      href={trackingHref}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="font-mono text-foreground hover:underline"
                                    >
                                      {s.tracking_number}
                                      <ExternalLink className="ml-1 inline h-3 w-3" />
                                    </a>
                                  ) : (
                                    <span className="font-mono">{s.tracking_number}</span>
                                  )}
                                  {shipments.length > 1 && s.tracking_status && (
                                    <span className="ml-1 text-muted-foreground">
                                      · {TRACKING_STATUS_LABELS[s.tracking_status]}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                            {shipments.length === 1 && po.tracking_status && (
                              <div className="mt-0.5 text-muted-foreground">
                                {TRACKING_STATUS_LABELS[po.tracking_status]}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-muted-foreground">No tracking</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {arrival ? (
                          <>
                            {formatDateOnly(arrival)}
                            {po.tracking_estimated_delivery && (
                              <span className="ml-1 text-xs">(carrier est.)</span>
                            )}
                          </>
                        ) : (
                          'Not set'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {!!productionOrders?.length && (
        <Card>
          <CardHeader>
            <CardTitle>Production orders</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Machine</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Remaining</TableHead>
                  <TableHead>Arrival</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {productionOrders.map((order) => {
                  const remaining = Math.max(0, order.quantity - (order.actual_quantity ?? 0));
                  const isDraft = order.status === 'draft';
                  return (
                    <TableRow key={order.id}>
                      <TableCell>
                        <Link to="/mfg/work-schedules" className="text-foreground hover:underline">
                          {order.title}
                        </Link>
                        {order.source === 'reorder_point' && (
                          <Badge variant="outline" className="ml-2">
                            Auto
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {order.work_center?.name ?? (
                          <span className="text-muted-foreground">Not assigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={isDraft ? 'warning-soft' : 'info-soft'}>
                          {PRODUCTION_STATUS_LABELS[order.status] ?? order.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {remaining.toLocaleString()}
                        {(order.actual_quantity ?? 0) > 0 && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            of {order.quantity.toLocaleString()}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(order.planned_end)}
                        {isDraft && <span className="ml-1 text-xs">(not on order until confirmed)</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>By location</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonRows />
          ) : !data || data.length === 0 ? (
            <EmptyState
              icon={Boxes}
              title="No inventory yet"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Location</TableHead>
                  <TableHead>Lot</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">
                      {row.locations?.name ?? '—'}
                      {row.locations?.code && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({row.locations.code})
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {row.lots?.lot_number ?? '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {stockQty(Number(row.quantity))}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(row.updated_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ItemMovementsCard
        itemId={itemId}
        baseUom={baseUom}
        onHand={isLoading ? null : stats.onHand}
      />
    </div>
  );
}

function SalesTab({ itemId }: { itemId: string }) {
  const { data, isLoading } = useItemSalesOrderLines(itemId);

  // Velocity = units moved across the visible (last 200) sales orders.
  // A real implementation should bucket by week + filter to fulfilled
  // orders only; this is the day-one summary so the panel isn't empty.
  const stats = useMemo(() => {
    const rows = data ?? [];
    const openStatuses = new Set([
      'draft',
      'pending_payment',
      'allocated',
      'picking',
      'packed',
      'on_hold',
    ]);
    const open = rows.filter((r) => openStatuses.has(r.sales_orders?.status ?? ''));
    const totalUnits = rows.reduce((acc, r) => acc + (r.quantity ?? 0), 0);
    const totalRevenue = rows.reduce((acc, r) => acc + (r.line_total_cents ?? 0), 0);
    return {
      total: rows.length,
      open: open.length,
      openUnits: open.reduce((acc, r) => acc + (r.quantity ?? 0), 0),
      totalUnits,
      totalRevenue,
    };
  }, [data]);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-4">
        <SummaryCard label="Open orders" value={stats.open.toLocaleString()} />
        <SummaryCard label="Open units" value={stats.openUnits.toLocaleString()} />
        <SummaryCard label="Lifetime units" value={stats.totalUnits.toLocaleString()} />
        <SummaryCard label="Lifetime revenue" value={formatCurrency(stats.totalRevenue)} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Order history</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonRows />
          ) : !data || data.length === 0 ? (
            <EmptyState
              icon={ShoppingCart}
              title="No sales orders"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SO #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Ordered</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Shipped</TableHead>
                  <TableHead className="text-right">Line total</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((row) => {
                  const so = row.sales_orders;
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">{so?.so_number ?? '—'}</TableCell>
                      <TableCell>{so?.customers?.name ?? so?.customer_email ?? '—'}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {so ? formatDate(so.ordered_at) : '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {Number(row.quantity).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {Number(row.shipped_quantity).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(row.line_total_cents)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="muted">{so?.status ?? '—'}</Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ProcurementTab({
  itemId,
  baseUom,
  newPoAction,
}: {
  itemId: string;
  baseUom: string;
  newPoAction?: React.ReactNode;
}) {
  const { data, isLoading } = useItemPurchaseOrderLines(itemId);
  const { data: costSummary } = useItemCostSummary(itemId);
  const { data: costHistory, isLoading: costLoading } = useItemCostHistory(itemId);
  const { data: purchaseCards } = usePurchaseCards();

  const stats = useMemo(() => {
    const rows = data ?? [];
    const open = rows.filter(
      (r) => !PO_CLOSED_STATUSES.includes((r.purchase_orders?.status ?? 'delivered') as PoStatus),
    );
    return {
      open: open.length,
      openUnits: open.reduce(
        (acc, r) =>
          acc +
          Math.max(0, (r.quantity ?? 0) - (r.received_quantity ?? 0)) * (Number(r.conversion_to_base) || 1),
        0,
      ),
    };
  }, [data]);

  return (
    <div className="space-y-6">
      {newPoAction && <div className="flex justify-end">{newPoAction}</div>}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard label="Open POs" value={stats.open.toLocaleString()} />
        <SummaryCard label="On order" value={formatQtyUom(Math.round(stats.openUnits), baseUom)} />
        <SummaryCard
          label={`Last landed cost / ${baseUom}`}
          value={formatUnitCostCents(costSummary?.last_cost_cents_exact ?? costSummary?.last_cost_cents)}
          detail={<LandedCostBreakdown parts={lastLandedCostParts(costSummary)} always />}
        />
        <SummaryCard
          label={`Avg landed cost (12 mo) / ${baseUom}`}
          value={formatUnitCostCents(costSummary?.avg_cost_12m_cents_exact ?? costSummary?.avg_cost_12m_cents)}
          detail={<LandedCostBreakdown parts={avgLandedCostParts(costSummary)} always />}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Cost history</CardTitle>
        </CardHeader>
        <CardContent>
          {costLoading ? (
            <SkeletonRows />
          ) : !costHistory || costHistory.length === 0 ? (
            <EmptyState
              icon={Truck}
              title="No receipts yet"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Received</TableHead>
                  <TableHead>Receipt #</TableHead>
                  <TableHead>PO #</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Item cost</TableHead>
                  <TableHead
                    className="text-right"
                    title="Tariff on the PO line plus its even share of the PO's shipping and other charges, per unit"
                  >
                    Tariff &amp; charges
                  </TableHead>
                  <TableHead
                    className="text-right"
                    title="Item cost + tariff + shipping and other charges"
                  >
                    Landed cost
                  </TableHead>
                  <TableHead className="text-right">Ext. landed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costHistory.map((row) => {
                  const gr = row.goods_receipts;
                  const po = gr?.purchase_orders;
                  const rowUom = row.po_lines?.uom || baseUom;
                  const packed = isPackUnit(Number(row.conversion_to_base));
                  const parts = receiptLandedCostParts(row);
                  const extras = parts ? parts.tariff + parts.shipping + parts.other : 0;
                  return (
                    <TableRow key={row.id} className={row.status === 'reversed' ? 'opacity-60' : undefined}>
                      <TableCell className="text-muted-foreground">{formatDate(gr?.received_at)}</TableCell>
                      <TableCell className="font-mono text-xs">{gr?.receipt_number ?? '—'}</TableCell>
                      <TableCell className="font-mono text-xs">
                        <PoNumberLink po={po} />
                      </TableCell>
                      <TableCell>{po?.vendors?.name ?? '—'}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {purchaseMethodLabel(po?.purchase_method, po?.purchase_card_id, purchaseCards) ?? '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatQtyUom(Number(row.quantity), rowUom)}
                        {packed && (
                          <span className="block text-xs text-muted-foreground">
                            = {formatQtyUom(Number(row.base_quantity), baseUom)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.status === 'reversed' ? (
                          <Badge variant="muted">Reversed</Badge>
                        ) : (
                          <span className="text-muted-foreground">Posted</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(row.unit_cost_cents)}
                        {row.unit_cost_cents != null && (
                          <span className="text-xs text-muted-foreground">/{rowUom}</span>
                        )}
                        {packed && row.unit_cost_cents != null && (
                          <span className="block text-xs text-muted-foreground">
                            {formatUnitCostCents(row.unit_cost_cents / Number(row.conversion_to_base))}/{baseUom}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {!parts || extras <= 0 ? (
                          '—'
                        ) : (
                          <>
                            +{formatUnitCostCents(extras)}
                            <span className="block text-xs">
                              {[
                                parts.tariff > 0
                                  ? `tariff ${formatUnitCostCents(parts.tariff)}`
                                  : null,
                                parts.shipping > 0
                                  ? `shipping ${formatUnitCostCents(parts.shipping)}`
                                  : null,
                                parts.other > 0
                                  ? `other ${formatUnitCostCents(parts.other)}`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          </>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {!parts ? (
                          '—'
                        ) : (
                          <>
                            {formatUnitCostCents(parts.landed)}
                            <span className="text-xs text-muted-foreground">/{rowUom}</span>
                            {packed && (
                              <span className="block text-xs text-muted-foreground">
                                {formatUnitCostCents(parts.landed / Number(row.conversion_to_base))}
                                /{baseUom}
                              </span>
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {!parts ? '—' : formatCurrency(Math.round(parts.landed * row.quantity))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Purchase order lines</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonRows />
          ) : !data || data.length === 0 ? (
            <EmptyState
              icon={Truck}
              title="No purchase orders"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO #</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Ordered</TableHead>
                  <TableHead>Expected</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right">Unit cost</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((row) => {
                  const po = row.purchase_orders;
                  const rowUom = row.uom || baseUom;
                  const conv = Number(row.conversion_to_base) || 1;
                  const packed = isPackUnit(conv);
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">
                        <PoNumberLink po={po} />
                      </TableCell>
                      <TableCell>{po?.vendors?.name ?? '—'}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDateOnly(po?.ordered_at)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDateOnly(po?.expected_at)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatQtyUom(Number(row.quantity), rowUom)}
                        {packed && (
                          <span className="block text-xs text-muted-foreground">
                            = {formatQtyUom(Math.round(Number(row.quantity) * conv), baseUom)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {formatQtyUom(Number(row.received_quantity), rowUom)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatUnitCurrency(row.unit_price_cents)}
                        <span className="text-xs text-muted-foreground">/{rowUom}</span>
                      </TableCell>
                      <TableCell>
                        {po?.status ? (
                          <Badge variant={PO_STATUS_VARIANTS[po.status as PoStatus] ?? 'muted'}>
                            {PO_STATUS_LABELS[po.status as PoStatus] ?? po.status}
                          </Badge>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ManufacturingTab({ itemId }: { itemId: string }) {
  const { data: producing, isLoading: pLoading } = useItemProducingWorkOrders(itemId);
  const { data: consuming, isLoading: cLoading } = useItemConsumingWorkOrders(itemId);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Work orders producing this item</CardTitle>
        </CardHeader>
        <CardContent>
          {pLoading ? (
            <SkeletonRows />
          ) : !producing || producing.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title="No work orders"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>WO #</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead>Scheduled</TableHead>
                  <TableHead>Actual</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {producing.map((wo) => (
                  <TableRow key={wo.id}>
                    <TableCell className="font-mono text-xs">{wo.wo_number}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Number(wo.quantity).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {wo.scheduled_start ? formatDate(wo.scheduled_start) : '—'}
                      {wo.scheduled_end && ` → ${formatDate(wo.scheduled_end)}`}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {wo.actual_start ? formatDate(wo.actual_start) : '—'}
                      {wo.actual_end && ` → ${formatDate(wo.actual_end)}`}
                    </TableCell>
                    <TableCell>
                      <Badge variant="muted">{wo.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Work orders consuming this item</CardTitle>
        </CardHeader>
        <CardContent>
          {cLoading ? (
            <SkeletonRows />
          ) : !consuming || consuming.length === 0 ? (
            <EmptyState
              icon={Factory}
              title="No consumption recorded"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>WO #</TableHead>
                  <TableHead>Producing</TableHead>
                  <TableHead className="text-right">Qty consumed</TableHead>
                  <TableHead>Recorded</TableHead>
                  <TableHead>WO status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {consuming.map((row) => {
                  const wo = row.work_orders;
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">{wo?.wo_number ?? '—'}</TableCell>
                      <TableCell>
                        {wo?.items ? (
                          <Link
                            to={`/catalog/items/${wo.items.id}`}
                            className="text-foreground hover:underline"
                          >
                            {wo.items.sku} · {wo.items.name}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {Number(row.quantity).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDateTime(row.recorded_at)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="muted">{wo?.status ?? '—'}</Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Selectable document categories. The slug is what we persist in
// `item_documents.kind`; the label is what the picker shows. Data sheets
// map to `spec_sheet`, CAD files to `drawing`.
const DOCUMENT_KIND_OPTIONS: { value: ItemDocumentKind; label: string }[] = [
  { value: 'spec_sheet', label: 'Data sheet' },
  { value: 'drawing', label: 'CAD / Drawing' },
  { value: 'photo', label: 'Photo' },
  { value: 'manual', label: 'Manual' },
  { value: 'other', label: 'Other' },
];

const DOCUMENT_KIND_LABELS: Record<string, string> = Object.fromEntries(
  DOCUMENT_KIND_OPTIONS.map((o) => [o.value, o.label]),
);

// Anything over this we reject client-side before hitting Storage.
const MAX_DOCUMENT_BYTES = 100 * 1024 * 1024;

function DocumentsTab({ itemId }: { itemId: string }) {
  const { role } = useAuth();
  const canEdit = canWrite(role as Role | null, 'inventory');
  const { data, isLoading } = useItemDocuments(itemId);
  const upload = useUploadItemDocument();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  // Category applied to the next upload batch.
  const [uploadKind, setUploadKind] = useState<ItemDocumentKind>('spec_sheet');

  const handleFiles = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      // Sequential so we don't slam Storage when many files are picked
      // at once — mirrors the project / task attachment uploaders.
      for (const file of Array.from(files)) {
        if (file.size > MAX_DOCUMENT_BYTES) {
          toast.error(`"${file.name}" exceeds the 100 MB limit.`);
          continue;
        }
        await upload.mutateAsync({ itemId, file, kind: uploadKind });
      }
      toast.success('Document uploaded.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Documents</CardTitle>
          </div>
          {canEdit && (
            <div className="flex shrink-0 items-center gap-2">
              <Select
                value={uploadKind}
                onValueChange={(v) => setUploadKind(v as ItemDocumentKind)}
              >
                <SelectTrigger className="h-9 w-[150px] text-sm">
                  {DOCUMENT_KIND_LABELS[uploadKind] ?? 'Type'}
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_KIND_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Paperclip className="h-3.5 w-3.5" />
                )}
                {uploading ? 'Uploading…' : 'Add document'}
              </Button>
              <FolderUploadButton
                size="sm"
                disabled={uploading}
                onFiles={(files) => void handleFiles(files)}
              />
              <input
                ref={inputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => void handleFiles(e.target.files)}
              />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonRows />
        ) : !data || data.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No documents"
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Label</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Added</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((doc) => (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  itemId={itemId}
                  canEdit={canEdit}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function DocumentRow({
  doc,
  itemId,
  canEdit,
}: {
  doc: ItemDocument;
  itemId: string;
  canEdit: boolean;
}) {
  const signedUrlQ = useItemDocumentSignedUrl(doc.storage_bucket, doc.storage_path);
  const deleteDoc = useDeleteItemDocument();

  // Hosted files resolve to a short-lived signed URL; legacy rows may
  // instead carry a plain external `url`.
  const href = doc.storage_path ? signedUrlQ.data : doc.url;
  const isHosted = !!doc.storage_path;

  const onDelete = async () => {
    if (!window.confirm(`Delete "${doc.label}"? This cannot be undone.`)) return;
    try {
      await deleteDoc.mutateAsync({
        id: doc.id,
        itemId,
        storageBucket: doc.storage_bucket,
        storagePath: doc.storage_path,
      });
      toast.success('Document deleted.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      toast.error(msg);
    }
  };

  return (
    <TableRow>
      <TableCell className="font-medium">{doc.label}</TableCell>
      <TableCell>
        <Badge variant="outline" className="gap-1">
          <DocumentKindIcon kind={doc.kind} />
          {DOCUMENT_KIND_LABELS[doc.kind] ?? doc.kind.replace('_', ' ')}
        </Badge>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {doc.mime_type ?? '—'}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatDate(doc.created_at)}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              download={isHosted ? doc.label : undefined}
              className="inline-flex items-center gap-1 text-xs text-atmosphere-dark hover:underline"
            >
              {isHosted ? (
                <>
                  Download
                  <Download className="h-3 w-3" />
                </>
              ) : (
                <>
                  Open
                  <ExternalLink className="h-3 w-3" />
                </>
              )}
            </a>
          ) : isHosted && signedUrlQ.isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          ) : null}
          {canEdit && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={onDelete}
              disabled={deleteDoc.isPending}
              aria-label={`Delete ${doc.label}`}
            >
              {deleteDoc.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// =====================================================================
// Helpers
// =====================================================================

function BackLink() {
  return (
    <Link
      to="/catalog/items"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to items
    </Link>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="text-right text-foreground">{value}</span>
    </div>
  );
}

/**
 * Inline-editable status badge for the page header. Mirrors the badge
 * the read-only view used to render but, for admins, doubles as a
 * one-click way to change the lifecycle state without scrolling to
 * the Item details card.
 */
function HeaderStatusBadge({ item }: { item: Item }) {
  const { role } = useAuth();
  const canEdit = canWrite(role as Role | null, 'inventory');
  return <ItemStatusControl item={item} canEdit={canEdit} density="kv" align="right" />;
}

/**
 * Same shape as `KV` but for fields that are inline-editable. The
 * editor itself decides whether to render as a static value (non-admin)
 * or a click-to-edit affordance (admin); this row just gives it a
 * label + a right-aligned slot that matches every other entry in the
 * Item details card.
 */
function EditableKV({ label, editor }: { label: string; editor: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <div className="min-w-0 flex-1 text-right text-foreground">{editor}</div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: React.ReactNode;
  detail?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <p className="mt-1 text-2xl font-medium tabular-nums tracking-headline text-foreground">
          {value}
        </p>
        {detail}
      </CardContent>
    </Card>
  );
}

/**
 * What a landed cost is made of, per stock unit: the item price plus any
 * tariff, shipping share and other-charge share from the PO. Nothing is
 * rendered when there are no extras (the landed cost is the item price)
 * unless `always` is set, which spells that out instead.
 */
function LandedCostBreakdown({
  parts,
  always = false,
}: {
  parts: LandedCostParts | null;
  always?: boolean;
}) {
  if (!parts) return null;
  const extras = [
    parts.tariff > 0 ? `tariff ${formatUnitCostCents(parts.tariff)}` : null,
    parts.shipping > 0 ? `shipping ${formatUnitCostCents(parts.shipping)}` : null,
    parts.other > 0 ? `other charges ${formatUnitCostCents(parts.other)}` : null,
  ].filter((s): s is string => !!s);
  if (extras.length === 0) {
    return always ? (
      <span className="mt-1 block text-xs font-normal text-muted-foreground">
        Item price only — no tariff or freight.
      </span>
    ) : null;
  }
  return (
    <span
      className="mt-1 block text-xs font-normal tabular-nums text-muted-foreground"
      title="Landed cost = item price + tariff + even share of the PO's shipping and other charges"
    >
      item {formatUnitCostCents(parts.goods)} + {extras.join(' + ')}
    </span>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function DocumentKindIcon({ kind }: { kind: string }) {
  switch (kind) {
    case 'photo':
      return <ImageIcon className="h-3 w-3" />;
    case 'drawing':
    case 'spec_sheet':
    case 'manual':
    default:
      return <FileText className="h-3 w-3" />;
  }
}
